import { Box, Paper, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import { AwardChip } from "src/grading/components";
import { CategoryResult, EVENT_LABEL, EventId } from "src/models/sessions";
import EventIcon from "../Sessions/planner/EventIcon";

export const formatValue = (eventId: string, value: number) =>
  eventId === "pft-mile"
    ? `${Math.floor(value / 60)}:${String(Math.round(value % 60)).padStart(2, "0")}`
    : String(value);

const unitOf = (eventId: string, mode?: string) => {
  if (mode) return mode;
  if (eventId === "pft-mile") return "";
  if (eventId === "pft-plank") return "s";
  if (eventId.startsWith("pft-beep")) return "laps";
  return "reps";
};

interface Props {
  category: string;
  result?: CategoryResult;
  /** Same category in the previous window, when the page has it. */
  prior?: CategoryResult;
}

const Marker = ({
  left,
  label,
  tone,
}: {
  left: number;
  label: string;
  tone: "tierNational" | "tierPresidential";
}) => (
  <Box
    title={label}
    data-testid={`marker-${tone}`}
    sx={{
      position: "absolute",
      top: 9,
      left: `${left}%`,
      width: 3,
      height: 24,
      borderRadius: "2px",
      transform: "translateX(-1.5px)",
      bgcolor: (theme) => theme.palette[tone],
    }}
  />
);

const CardFrame = ({
  heading,
  icon,
  eventLabel,
  children,
}: {
  heading: string;
  icon?: EventId;
  eventLabel?: string;
  children: ReactNode;
}) => (
  <Paper
    component="section"
    aria-label={heading}
    sx={{
      p: 2.5,
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      minWidth: 0,
    }}
  >
    <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
      {icon ? (
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            flex: "none",
            borderRadius: `${radius.control}px`,
            bgcolor: "primary.light",
            display: "grid",
            placeItems: "center",
          }}
        >
          <EventIcon eventId={icon} size={26} />
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="overline"
          component="p"
          sx={{ m: 0, color: "dangerInk", lineHeight: 1 }}
        >
          {heading}
        </Typography>
        {eventLabel ? (
          <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.3 }}>
            {eventLabel}
          </Typography>
        ) : null}
      </Box>
    </Box>
    {children}
  </Paper>
);

/**
 * One category of a student's result (design `result.cats`): event, big value, status chip,
 * benchmark track with the National and Presidential markers, prior window and next step.
 */
export const CategoryResultCard = ({ category, result, prior }: Props) => {
  const { t } = useTranslation();
  const heading = t(`categories.${category}`);

  if (!result) {
    return (
      <CardFrame heading={heading}>
        <Box
          sx={(theme) => ({
            p: 1.5,
            borderRadius: `${radius.control}px`,
            bgcolor: theme.palette.tokens.warningBg,
            color: theme.palette.tokens.warningFg,
            fontSize: 13,
            lineHeight: 1.45,
          })}
        >
          {t("results.noScore", {
            defaultValue:
              "No score yet. Score it at a station or schedule a make-up.",
          })}
        </Box>
      </CardFrame>
    );
  }

  const isMile = result.eventId === "pft-mile";
  const award = result.excluded
    ? "participation"
    : result.presidentialMet
      ? "presidential"
      : result.nationalMet
        ? "national"
        : null;
  const scaleMax =
    Math.max(
      result.presidentialThreshold,
      result.nationalThreshold,
      result.value,
      prior?.value ?? 0,
    ) * 1.15 || 1;
  const pos = (v: number) => Math.min(100, Math.max(0, (v / scaleMax) * 100));
  const unit = unitOf(result.eventId, result.mode);
  const eventLabel = `${
    EVENT_LABEL[result.eventId as EventId] ?? result.eventId
  }${
    result.modification !== "none"
      ? ` · ${t(`modification.${result.modification}`)}`
      : ""
  }`;

  const showPrior = prior && prior.eventId === result.eventId;
  const delta = showPrior ? result.value - prior.value : 0;
  const better = isMile ? delta < 0 : delta > 0;
  const deltaText = showPrior
    ? `${better ? "+" : delta === 0 ? "" : "−"}${
        isMile ? formatValue("pft-mile", Math.abs(delta)) : Math.abs(delta)
      }`
    : "";
  const hint =
    !result.excluded && !result.presidentialMet && result.nextTierGap !== null
      ? t(isMile ? "results.nextBenchmarkTime" : "results.nextBenchmark", {
          gap: isMile
            ? formatValue("pft-mile", result.nextTierGap)
            : result.nextTierGap,
          tier: result.nationalMet
            ? t("results.tier.presidential")
            : t("results.tier.national"),
        })
      : null;

  return (
    <CardFrame
      heading={heading}
      icon={
        result.eventId in EVENT_LABEL ? (result.eventId as EventId) : undefined
      }
      eventLabel={eventLabel}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 1.25,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
          <Typography variant="stat" component="p" sx={{ fontSize: 56 }}>
            {formatValue(result.eventId, result.value)}
          </Typography>
          {unit && (
            <Typography sx={{ color: "text.secondary", fontWeight: 700 }}>
              {unit}
            </Typography>
          )}
        </Box>
        {award ? (
          <AwardChip award={award} size="sm" />
        ) : (
          <Box
            component="span"
            sx={(theme) => ({
              px: "10px",
              py: "7px",
              borderRadius: `${radius.pill}px`,
              bgcolor: theme.palette.surfaceAlt,
              color: theme.palette.text.primary,
              fontSize: 10,
              fontWeight: 800,
              lineHeight: 1,
              textTransform: "uppercase",
              fontStretch: fontStretch.heading,
              whiteSpace: "nowrap",
            })}
          >
            {t("common.notYet")}
          </Box>
        )}
      </Box>

      {result.excluded ? (
        <Box
          sx={(theme) => ({
            p: 1.5,
            borderRadius: `${radius.control}px`,
            bgcolor: theme.palette.surfaceAlt,
            color: theme.palette.text.secondary,
            fontSize: 13,
            lineHeight: 1.45,
          })}
        >
          {t("results.adaptedNote", {
            defaultValue:
              "Adapted events aren't compared to the benchmarks. They count toward Participation.",
          })}
        </Box>
      ) : (
        <>
          <Box
            role="img"
            aria-label={`${t("results.tier.national")} ${formatValue(result.eventId, result.nationalThreshold)}, ${t("results.tier.presidential")} ${formatValue(result.eventId, result.presidentialThreshold)}`}
            sx={{ position: "relative", height: 40, mt: 0.75 }}
          >
            <Box
              sx={(theme) => ({
                position: "absolute",
                left: 0,
                right: 0,
                top: 16,
                height: 10,
                borderRadius: `${radius.pill}px`,
                bgcolor: theme.palette.track,
              })}
            />
            <Marker
              left={pos(result.nationalThreshold)}
              label={t("results.tier.national")}
              tone="tierNational"
            />
            <Marker
              left={pos(result.presidentialThreshold)}
              label={t("results.tier.presidential")}
              tone="tierPresidential"
            />
            {showPrior ? (
              <Box
                title={t("results.priorWindow")}
                data-testid="prior-marker"
                sx={(theme) => ({
                  position: "absolute",
                  left: `${pos(prior.value)}%`,
                  top: 13,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: `2.5px solid ${theme.palette.text.disabled}`,
                  bgcolor: theme.palette.background.paper,
                  transform: "translateX(-50%)",
                })}
              />
            ) : null}
            <Box
              sx={(theme) => ({
                position: "absolute",
                left: `${pos(result.value)}%`,
                top: 9,
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: theme.palette.text.primary,
                border: `4px solid ${theme.palette.background.paper}`,
                boxShadow: `0 0 0 2px ${theme.palette.text.primary}`,
                transform: "translateX(-50%)",
                transition: "left .6s",
              })}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <Box component="span" sx={{ color: "dangerInk" }}>
              {t("results.tier.national")}{" "}
              {formatValue(result.eventId, result.nationalThreshold)}
            </Box>
            <Box component="span" sx={{ color: "primaryInk" }}>
              {t("results.tier.presidential")}{" "}
              {formatValue(result.eventId, result.presidentialThreshold)}
            </Box>
          </Box>
          {result.alternateThresholds && (
            <Typography
              variant="caption"
              component="p"
              sx={{ color: "text.secondary", m: 0 }}
            >
              {`${result.alternateThresholds.sex} table: ${formatValue(result.eventId, result.alternateThresholds.nationalThreshold)} / ${formatValue(result.eventId, result.alternateThresholds.presidentialThreshold)}`}
            </Typography>
          )}
          {showPrior && (
            <Box
              data-testid="prior-row"
              sx={(theme) => ({
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                pt: 1.25,
                borderTop: `1px solid ${theme.palette.divider}`,
                fontSize: 12.5,
                fontWeight: 600,
              })}
            >
              <Box component="span" sx={{ color: "text.secondary" }}>
                {`○ ${t("results.priorWindow")} ${formatValue(prior.eventId, prior.value)}`}
              </Box>
              <Box
                component="span"
                sx={{
                  fontWeight: 800,
                  color: better ? "successInk" : "text.secondary",
                }}
              >
                {deltaText}
              </Box>
            </Box>
          )}
          {hint && (
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {`${t("common.notYet")}: ${hint}`}
            </Typography>
          )}
        </>
      )}
    </CardFrame>
  );
};

export default CategoryResultCard;
